/**
 * Persistent-storage request (2026-08-31).
 *
 * WHY THIS EXISTS: Safari evicts script-writable storage after ~7 days without
 * use. For a rig that gigs weekly-to-fortnightly that is not academic - it
 * means opening OPButtons at a venue to a blank app: no cached shell, no
 * layouts, no pairings. It was the single strongest argument for abandoning
 * the hosted PWA and serving it from OPBridge instead, at the cost of a full
 * origin reset and losing central hotfixes.
 *
 * An origin granted PERSISTENT storage is exempt from that eviction. Measured
 * on the operator's iPad (8th gen, iPadOS 26.6.1, installed to Home Screen):
 * `persisted()` initially returned **false**, `persist()` returned **true**,
 * and a follow-up `persisted()` then read **true**. So WebKit both implements
 * and GRANTS this to an installed web app - which retires the eviction risk
 * for a one-line call, and with it the main reason to move origins.
 *
 * Deliberately best-effort and non-blocking:
 *  - Never throws. A browser without the API, or one that denies, is a normal
 *    supported state, not an error - the app works either way, it is just
 *    evictable.
 *  - Never awaited on the boot path.
 *  - The RESULT IS SURFACED, not swallowed, so pre-flight can report it. A
 *    silent grant and a silent denial look identical otherwise, and "storage
 *    might have been evicted" is exactly the kind of slow failure this project
 *    keeps getting caught by.
 *
 * NOTE for a fresh install: iOS grants this readily to an installed app but a
 * plain Safari tab may be denied. That is fine and expected - the tab is not
 * the surface that has to survive a fortnight in a flight case.
 */

import { createSignal } from 'solid-js';

export type PersistState =
  | 'unknown'      // not asked yet
  | 'persisted'    // granted - exempt from eviction
  | 'denied'       // the browser implements it and said no
  | 'unsupported'; // no Storage API here

const [persistStateSig, setPersistState] = createSignal<PersistState>('unknown');

/** Current persistent-storage state. Read by pre-flight. */
export const persistState = persistStateSig;

/** Ask the browser to make this origin's storage exempt from eviction.
 *  Idempotent and safe to call more than once: if it is already persisted the
 *  spec resolves true without re-prompting. */
export async function requestPersistentStorage(): Promise<PersistState> {
  try {
    const s = navigator.storage;
    if (!s || typeof s.persist !== 'function' || typeof s.persisted !== 'function') {
      setPersistState('unsupported');
      return 'unsupported';
    }
    // Check first - an already-persisted origin needs no request at all.
    if (await s.persisted()) {
      setPersistState('persisted');
      return 'persisted';
    }
    await s.persist();
    // Trust the STATE, not the return value: re-read rather than believing
    // what persist() reported. Cheap, and it is the difference between "the
    // call succeeded" and "the storage is actually persistent".
    const now = await s.persisted();
    const next: PersistState = now ? 'persisted' : 'denied';
    setPersistState(next);
    return next;
  } catch {
    setPersistState('unsupported');
    return 'unsupported';
  }
}

/** Bytes this app holds on the device (cache + storage), or null where the
 *  browser will not say. 2026-10-18: a full iPad wiped the app and the
 *  operator asked "maybe opbuttons had a leak?" - by the code it cannot
 *  (no IndexedDB, no runtime cache, ~4 MB precache, localStorage capped
 *  ~5 MB), and this puts the measured number in pre-flight so the question
 *  is answered on the device rather than by reasoning. */
export async function readStorageUsage(): Promise<number | null> {
  try {
    const est = await navigator.storage?.estimate?.();
    return typeof est?.usage === 'number' ? est.usage : null;
  } catch {
    return null;
  }
}
